import axios from 'axios';
import { API_URL } from '../config/apiConfig';
import type { OCRResult, Gender, DocumentType } from '../types/checkin';
import { ISO_NATIONALITIES } from '../utils/referenceData';

const api = axios.create({
  baseURL: API_URL,
  timeout: 60000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * Parse gender from various formats
 */
function parseGender(raw: string): Gender | undefined {
  if (!raw) return undefined;
  const v = raw.toUpperCase().trim();
  if (v === 'M' || v === 'MALE' || v === 'MASCULIN') return 'Male';
  if (v === 'F' || v === 'FEMALE' || v === 'FEMININ' || v === 'FÉMININ') return 'Female';
  return undefined;
}

/**
 * Parse date from various formats into YYYY-MM-DD
 */
function parseDate(raw: string): string | undefined {
  if (!raw) return undefined;
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  // DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
  const m = raw.match(/(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
  if (m) return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  // DD/MM/YY
  const m1b = raw.match(/(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2})$/);
  if (m1b) {
    const yr = parseInt(m1b[3]) > 50 ? `19${m1b[3]}` : `20${m1b[3]}`;
    return `${yr}-${m1b[2].padStart(2, '0')}-${m1b[1].padStart(2, '0')}`;
  }
  // YYYYMMDD
  const m2 = raw.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (m2) return `${m2[1]}-${m2[2]}-${m2[3]}`;
  // YYMMDD (MRZ format)
  const m2b = raw.match(/^(\d{2})(\d{2})(\d{2})$/);
  if (m2b) {
    const yr = parseInt(m2b[1]) > 50 ? `19${m2b[1]}` : `20${m2b[1]}`;
    return `${yr}-${m2b[2]}-${m2b[3]}`;
  }
  // "29 Me/Mai 96" or "31 Jan/Jan 32" or "01 Fev/Fév 22"
  const months: Record<string, string> = {
    jan: '01', fev: '02', fév: '02', feb: '02', mar: '03', avr: '04', apr: '04',
    mai: '05', may: '05', me: '05', juin: '06', jun: '06', juil: '07', jul: '07',
    aou: '08', août: '08', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12', déc: '12',
    jdan: '01', // OCR misread of Jan
  };
  const m3 = raw.match(/(\d{1,2})\s*(?:\/?\s*)([A-Za-zÀ-ÿ]+)(?:\s*\/\s*[A-Za-zÀ-ÿ]+)?\s+(\d{2,4})/);
  if (m3) {
    const monthKey = m3[2].toLowerCase().replace(/[^a-zà-ÿ]/g, '');
    const mm = months[monthKey];
    if (mm) {
      const yr = m3[3].length === 2 ? (parseInt(m3[3]) > 50 ? `19${m3[3]}` : `20${m3[3]}`) : m3[3];
      return `${yr}-${mm}-${m3[1].padStart(2, '0')}`;
    }
  }
  return undefined;
}

/**
 * Parse document type
 */
function parseDocumentType(raw: string): DocumentType | undefined {
  if (!raw) return undefined;
  const v = raw.toUpperCase();
  if (v.includes('PASSEPORT') || v.includes('PASSPORT')) return 'Passport';
  if (v.includes('PERMIS') || v.includes('LICENSE') || v.includes('DRIVER')) return 'Driving License';
  if (v.includes('CNI') || v.includes('NATIONAL') || v.includes('IDENTITY') || v.includes('IDENTITE')) return 'National ID Card (CNI)';
  if (v.includes('VISA')) return 'Visa';
  return 'Passport'; // default for most hotel check-ins
}

const NATIONALITY_CODE_MAP: Record<string, string> = {
  AGO: 'Angolaise',
  ARG: 'Argentine',
  AUS: 'Australienne',
  AUT: 'Autrichienne',
  BEL: 'Belge',
  BEN: 'Béninoise',
  BFA: 'Burkinabè',
  BDI: 'Burundaise',
  BRA: 'Brésilienne',
  CAF: 'Centrafricaine',
  CAN: 'Canadienne',
  CHE: 'Suisse',
  CHN: 'Chinoise',
  CIV: 'Ivoirienne',
  CMR: 'Camerounaise',
  COD: 'Congolaise (RDC)',
  COG: 'Congolaise (RC)',
  DEU: 'Allemande',
  DJI: 'Djiboutienne',
  DZA: 'Algérienne',
  EGY: 'Égyptienne',
  ESP: 'Espagnole',
  ETH: 'Éthiopienne',
  FRA: 'Française',
  GAB: 'Gabonaise',
  GBR: 'Britannique',
  GHA: 'Ghanéenne',
  GIN: 'Guinéenne',
  GMB: 'Gambienne',
  GNQ: 'Équato-Guinéenne',
  GRC: 'Grecque',
  HAI: 'Haïtienne',
  IND: 'Indienne',
  IRL: 'Irlandaise',
  ITA: 'Italienne',
  JPN: 'Japonaise',
  KEN: 'Kényane',
  KOR: 'Sud-Coréenne',
  LBN: 'Libanaise',
  LBR: 'Libérienne',
  MAR: 'Marocaine',
  MDG: 'Malgache',
  MLI: 'Malienne',
  MRT: 'Mauritanienne',
  MUS: 'Mauricienne',
  MWI: 'Malawienne',
  MEX: 'Mexicaine',
  NAM: 'Namibienne',
  NER: 'Nigérienne',
  NGA: 'Nigériane',
  NOR: 'Norvégienne',
  NZL: 'Néo-Zélandaise',
  POL: 'Polonaise',
  PRT: 'Portugaise',
  ROU: 'Roumaine',
  RUS: 'Russe',
  RWA: 'Rwandaise',
  SDN: 'Soudanaise',
  SEN: 'Sénégalaise',
  SLE: 'Sierra-Léonaise',
  SOM: 'Somalienne',
  SSD: 'Sud-Soudanaise',
  SWE: 'Suédoise',
  TCD: 'Tchadienne',
  TGO: 'Togolaise',
  TUN: 'Tunisienne',
  TZA: 'Tanzanienne',
  UGA: 'Ougandaise',
  USA: 'Américaine',
  ZAF: 'Sud-Africaine',
  ZMB: 'Zambienne',
  ZWE: 'Zimbabwéenne',
};

const NATIONALITY_ALIAS_MAP: Record<string, string> = {
  'DEMOCRATIC REPUBLIC OF THE CONGO': 'Congolaise (RDC)',
  'DR CONGO': 'Congolaise (RDC)',
  'RDC': 'Congolaise (RDC)',
  'CONGO DRC': 'Congolaise (RDC)',
  'CONGO KINSHASA': 'Congolaise (RDC)',
  'REPUBLIC OF THE CONGO': 'Congolaise (RC)',
  'CONGO BRAZZAVILLE': 'Congolaise (RC)',
  'UNITED STATES': 'Américaine',
  'UNITED STATES OF AMERICA': 'Américaine',
  FRANCE: 'Française',
  GERMANY: 'Allemande',
  BELGIUM: 'Belge',
  CANADA: 'Canadienne',
  CHINA: 'Chinoise',
  JAPAN: 'Japonaise',
  INDIA: 'Indienne',
  MOROCCO: 'Marocaine',
  ALGERIA: 'Algérienne',
  TUNISIA: 'Tunisienne',
  SENEGAL: 'Sénégalaise',
  CAMEROON: 'Camerounaise',
  RWANDA: 'Rwandaise',
  BURUNDI: 'Burundaise',
  UGANDA: 'Ougandaise',
  TANZANIA: 'Tanzanienne',
  'SOUTH AFRICA': 'Sud-Africaine',
  'UNITED KINGDOM': 'Britannique',
  'GREAT BRITAIN': 'Britannique',
  BRITAIN: 'Britannique',
  SWITZERLAND: 'Suisse',
  HAITI: 'Haïtienne',
  HAITIAN: 'Haïtienne',
  HAITIENNE: 'Haïtienne',
};

function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function normalizeNationality(raw: string): string | undefined {
  if (!raw) return undefined;

  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  const directMatch = ISO_NATIONALITIES.find(
    (value) => normalizeText(value) === normalizeText(trimmed),
  );
  if (directMatch) return directMatch;

  const primary = trimmed.includes('/') ? trimmed.split('/')[0].trim() : trimmed;
  const upper = primary.toUpperCase();

  if (NATIONALITY_CODE_MAP[upper]) {
    return NATIONALITY_CODE_MAP[upper];
  }

  const aliasKey = upper.replace(/[().-]/g, ' ').replace(/\s+/g, ' ').trim();
  if (NATIONALITY_ALIAS_MAP[aliasKey]) {
    return NATIONALITY_ALIAS_MAP[aliasKey];
  }

  if (/^[A-Z]{2,3}$/.test(upper)) {
    return 'Autre';
  }

  return primary;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function addScalarFields(fields: Record<string, string>, source: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(source)) {
    if (value == null || fields[key]) continue;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) fields[key] = trimmed;
      continue;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
      fields[key] = String(value);
    }
  }
}

function mergeStructuredFields(fields: Record<string, string>, source: unknown): void {
  if (!isRecord(source)) return;

  const queue: Record<string, unknown>[] = [source];
  const visited = new Set<Record<string, unknown>>();

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current || visited.has(current)) continue;
    visited.add(current);

    addScalarFields(fields, current);

    for (const key of ['data', 'result', 'fields', 'extracted', 'document', 'payload']) {
      const nested = current[key];
      if (isRecord(nested) && !visited.has(nested)) {
        queue.push(nested);
      }
    }
  }
}

function extractJSONFields(fields: Record<string, string>, raw: unknown): void {
  if (typeof raw !== 'string' || !raw.trim()) return;

  try {
    const match = raw.match(/\{[\s\S]*\}/);
    if (!match) return;

    const parsed = JSON.parse(match[0]);
    if (!isRecord(parsed)) return;

    const source = isRecord(parsed.fields) ? parsed.fields : parsed;
    mergeStructuredFields(fields, source);
  } catch {
    // Ignore malformed embedded JSON and continue with text heuristics.
  }
}

function extractRawText(response: any): string {
  const candidates = [
    response?.raw_text,
    response?.text,
    response?.ocr_text,
    response?.data?.raw_text,
    response?.data?.text,
    response?.data?.ocr_text,
    response?.result?.raw_text,
    response?.result?.text,
    response?.result?.ocr_text,
    response?.extracted?.raw_text,
    response?.extracted?.text,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  return '';
}

function normalizeConfidence(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;

  const parsed = typeof raw === 'number'
    ? raw
    : Number(String(raw).trim().replace('%', ''));

  if (!Number.isFinite(parsed) || parsed < 0) return undefined;
  return parsed <= 1 ? parsed * 100 : parsed;
}

function extractConfidence(response: any, fallback: number): number {
  const candidates = [
    response?.confidence,
    response?.score,
    response?.data?.confidence,
    response?.data?.score,
    response?.result?.confidence,
    response?.result?.score,
    response?.extracted?.confidence,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeConfidence(candidate);
    if (normalized !== undefined) {
      return Math.round(normalized * 100) / 100;
    }
  }

  return fallback;
}

/**
 * Extract fields from IZI API response. Handles all response formats.
 */
function extractFields(response: any): Record<string, string> {
  const fields: Record<string, string> = {};

  // 0. Pull structured fields from common response containers first.
  for (const source of [
    response,
    response?.data,
    response?.result,
    response?.extracted,
    response?.fields,
  ]) {
    mergeStructuredFields(fields, source);
  }

  // 1. Parse ai_analysis or analysis (JSON in markdown code block)
  for (const analysis of [
    response?.ai_analysis,
    response?.analysis,
    response?.data?.ai_analysis,
    response?.data?.analysis,
    response?.result?.ai_analysis,
    response?.result?.analysis,
  ]) {
    extractJSONFields(fields, analysis);
  }

  // 2. Use extracted.fields if present
  const ext = response.extracted;
  if (ext && typeof ext === 'object') {
    if (ext.document_type) fields.document_type = String(ext.document_type);
    const src = ext.fields || ext;
    for (const [k, v] of Object.entries(src)) {
      if (v && typeof v === 'string' && !fields[k]) fields[k] = v;
    }
  }

  // 3. Parse raw_text when AI analysis fails (common with real passport photos)
  const text = extractRawText(response);
  if (text && Object.keys(fields).length < 3) {
    const lines = text.split('\n').map((l: string) => l.trim());

    // Strategy: find label lines, then take the NEXT line as the value
    for (let i = 0; i < lines.length - 1; i++) {
      const line = lines[i];
      const next = lines[i + 1];

      // Surname/Nom — label line contains "Nom" or "Surname"
      if (!fields['Surname'] && /\b(?:Surname|Siyati|(?:^|\/)?\s*Nom)\b/i.test(line) && /^[A-Z][A-Z '-]+$/.test(next)) {
        fields['Surname'] = next.trim();
      }

      // Given Names/Prénoms
      if (!fields['Given Names'] && /\b(?:Given\s*Names?|Pr[ée]noms?|Non\s*\/)/i.test(line) && /^[A-Z][A-Za-zÀ-ÿ '-]+$/.test(next)) {
        fields['Given Names'] = next.trim();
      }

      // Sex — line contains "Sex" or "Sexe", next line starts with M or F
      if (!fields['Sex'] && /\b(?:Sexe?|Seks|Sex)\b/i.test(line) && /^(M|F)\b/.test(next)) {
        fields['Sex'] = next.charAt(0);
      }
    }

    // Nationality — word after nationality label (same line or next)
    if (!fields['Nationality']) {
      const nat = text.match(/(?:Nationalit[ée]|Nationality|Moun ki peyi)[^\n]*?\n?\s*([A-Z][a-zÀ-ÿ]+(?:ienne|aise|ese|an|ien|ed|aise)?)\b/i);
      if (nat) fields['Nationality'] = nat[1].trim();
    }

    // Date of birth — date after birth label
    if (!fields['Date of Birth']) {
      const dob = text.match(/(?:Date de naissance|date of birth|birth|Dat li f[eè]t)[^\n]*?\n?\s*(\d{1,2}\s+[A-Za-zÀ-ÿ/.]+\s*(?:\/\s*[A-Za-zÀ-ÿ]+\s+)?\d{2,4})/i);
      if (dob) fields['Date of Birth'] = dob[1].trim();
    }
    if (!fields['Date of Birth']) {
      const dob2 = text.match(/(?:Date de naissance|date of birth|birth)[^\n]*?\n?\s*(\d{1,2}[/\-\.]\d{1,2}[/\-\.]\d{2,4})/i);
      if (dob2) fields['Date of Birth'] = dob2[1].trim();
    }

    // Passport number — look for number near passport label, or standalone 6-9 digit number after "Passeport" line
    if (!fields['Passport No']) {
      const pno = text.match(/(?:Passeport|Passport|Paspo)\s*(?:nimewo|No|Number|N[°o])?[^\n]*?\n?\s*(?:R\s*:?\s*\n?\s*)?([A-Z]?\d{6,9})/i);
      if (pno) fields['Passport No'] = pno[1].trim();
    }

    // Expiry date
    if (!fields['Date of Expiry']) {
      const exp = text.match(/(?:expiration|expiry|paspo.*fini|valid.*until)[^\n]*?\n?\s*(\d{1,2}\s*[A-Za-zÀ-ÿ.]+\s*(?:\/\s*[A-Za-zÀ-ÿ]+\s+)?\d{2,4})/i);
      if (exp) fields['Date of Expiry'] = exp[1].trim();
    }

    // Document type
    if (!fields['document_type'] && /passeport|passport|paspo/i.test(text)) {
      fields['document_type'] = 'Passport';
    }

    // MRZ line 1 — P<XXXSURNAME<<GIVEN<NAMES
    const mrz = text.match(/P[<|]([A-Z]{3})([A-Z]+)[<]+([A-Z<]+)/);
    if (mrz) {
      if (!fields['Surname']) fields['Surname'] = mrz[2].replace(/</g, ' ').trim();
      if (!fields['Given Names']) fields['Given Names'] = mrz[3].replace(/</g, ' ').trim();
      if (!fields['document_type']) fields['document_type'] = 'Passport';
    }
    // MRZ line 2 — passport number, DOB, sex, expiry
    const mrz2 = text.match(/([A-Z]?\d{7,9})\d[A-Z]{3}(\d{6})\d(M|F)(\d{6})\d/);
    if (mrz2) {
      if (!fields['Passport No']) fields['Passport No'] = mrz2[1];
      if (!fields['Date of Birth']) fields['Date of Birth'] = mrz2[2];
      if (!fields['Sex']) fields['Sex'] = mrz2[3];
      if (!fields['Date of Expiry']) fields['Date of Expiry'] = mrz2[4];
    }
  }

  return fields;
}

/**
 * Map raw field names (English/French) to form data
 */
function mapToFormData(fields: Record<string, string>): OCRResult['extractedData'] {
  // Find a value by trying multiple possible key names
  const find = (...keys: string[]): string | undefined => {
    for (const k of keys) {
      if (fields[k]) return fields[k];
    }
    // Case-insensitive fallback
    const lowerFields: Record<string, string> = {};
    for (const [k, v] of Object.entries(fields)) {
      lowerFields[k.toLowerCase()] = v;
    }
    for (const k of keys) {
      if (lowerFields[k.toLowerCase()]) return lowerFields[k.toLowerCase()];
    }
    return undefined;
  };

  const surname = find('Surname', 'surname', 'Nom', 'nom', 'last_name', 'lastName', 'Family Name', 'family_name');
  const given = find('Given Names', 'Given Name', 'given_names', 'givenNames', 'Prenom', 'Prénom', 'Prénoms', 'first_name', 'firstName', 'First Name');
  const full = find('full_name', 'fullName', 'name', 'Name');

  const data: OCRResult['extractedData'] = {};

  // Name
  if (full) {
    data.fullName = full;
  } else if (surname && given) {
    data.fullName = `${surname} ${given}`;
  } else if (surname) {
    data.fullName = surname;
  }

  // Gender
  data.gender = parseGender(find('Sex', 'Sexe', 'sex', 'sexe', 'Gender', 'gender') || '');

  // Date of birth
  data.dateOfBirth = parseDate(find('Date of Birth', 'Date de naissance', 'date_of_birth', 'dateOfBirth', 'DOB', 'dob', 'birth_date') || '');

  // Nationality - clean up "CONGOLAISE / CONGOLESE" format
  const rawNat = find('Nationality', 'Nationalité', 'Nationalite', 'nationality', 'nationalite') || '';
  data.nationality = normalizeNationality(rawNat);

  // Document type
  data.documentType = parseDocumentType(find('document_type', 'documentType', 'Type', 'type', 'Document Type') || '');

  // Document number
  data.documentNumber = find('Passport No', 'Passport Number', 'passport_number', 'document_number', 'documentNumber',
    'Document No', 'No du Passeport', "N° du document", 'ID Number', 'License Number',
    'id_number', 'license_number', 'Numéro', 'numero', 'Number');

  // Expiry date
  data.documentExpiryDate = parseDate(find('Date of Expiry', 'Expiry Date', 'date_of_expiry', 'dateOfExpiry',
    'expiry_date', 'expiryDate', 'documentExpiryDate', 'expiry_date', "Date d'expiration",
    'Valid Until', 'valid_until', 'expiration_date') || '');

  return data;
}

export const ocrService = {
  /**
   * Read document image and extract identity fields using the active hotel OCR backend.
   * This path is intentionally routed only through the IZI-backed gateway endpoints.
   */
  async readDocument(file: File): Promise<OCRResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'passport');

    // Use vision/scan — best for passports and IDs (AI-powered structured extraction)
    const response = await api.post('/api/v1/ocr/izi/scan', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    const fields = extractFields(response.data);
    const data = mapToFormData(fields);
    const fieldCount = Object.values(data).filter(Boolean).length;
    const confidence = extractConfidence(response.data, Math.min(95, fieldCount > 0 ? 40 + fieldCount * 9 : 0));


    return {
      confidence,
      method: 'Text' as const,
      extractedData: data,
    };
  },

  /**
   * Try native local OCR (Tesseract + OpenCV + MRZ parsing).
   * Falls back gracefully if the native engine is unavailable (503).
   */
  async readNative(file: File): Promise<OCRResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'auto');

    // Try passport endpoint first (handles MRZ + ICAO validation)
    const response = await api.post('/api/v1/ocr/passport', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 45000,
      validateStatus: (s) => (s >= 200 && s < 300) || s === 422 || s === 503,
    });
    if (response.status === 422 || response.status === 503) {
      const err: any = new Error(
        response.status === 422
          ? 'Document illisible ou non reconnu'
          : 'Service OCR indisponible'
      );
      err.response = response;
      throw err;
    }

    const fields = extractFields(response.data);
    const data = mapToFormData(fields);
    const fieldCount = Object.values(data).filter(Boolean).length;
    const confidence = extractConfidence(response.data, Math.min(95, fieldCount > 0 ? 50 + fieldCount * 8 : 0));


    return {
      confidence,
      method: (response.data?.method === 'MRZ' ? 'MRZ' : 'Text') as OCRResult['method'],
      extractedData: data,
    };
  },

  /**
   * Main entry point: read document and fill form.
   * Priority: 1) Local native OCR  2) IZI vision scan  3) IZI analyze  4) IZI raw extract
   * Accepts the first high-confidence result, otherwise keeps the best usable extraction.
   */
  async processAuto(file: File): Promise<OCRResult> {
    const minimumAcceptedConfidence = 70;
    let bestResult: OCRResult | null = null;

    const countExtractedFields = (result: OCRResult): number =>
      Object.values(result.extractedData).filter(Boolean).length;

    const rememberBestResult = (result: OCRResult): void => {
      const fieldCount = countExtractedFields(result);
      if (fieldCount === 0) return;
      if (!bestResult) {
        bestResult = result;
        return;
      }

      const bestFieldCount = countExtractedFields(bestResult);
      if (fieldCount > bestFieldCount || (fieldCount === bestFieldCount && result.confidence > bestResult.confidence)) {
        bestResult = result;
      }
    };

    // 1. Try Tesseract MRZ first (fast, best for passport MRZ zones)
    let nativeResult: OCRResult | null = null;
    try {
      const result = await this.readNative(file);
      if (result.confidence >= minimumAcceptedConfidence && countExtractedFields(result) >= 3) {
        return result;
      }
      if (countExtractedFields(result) > 0) {
        nativeResult = result;
      }
      rememberBestResult(result);
    } catch (e: any) {
      // Tesseract not available, try next
    }

    // 2. Try EasyOCR AI engine (deep learning, multilingual)
    try {
      const aiFormData = new FormData();
      aiFormData.append('file', file);
      const aiResponse = await api.post('/api/v1/ocr/ai/extract', aiFormData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      const resp = aiResponse.data;
      if (resp?.success && resp?.data) {
        const d = resp.data;
        const aiData: OCRResult['extractedData'] = {
          fullName: d.fullName || '',
          gender: parseGender(d.gender || ''),
          dateOfBirth: d.dateOfBirth || '',
          nationality: normalizeNationality(d.nationality || ''),
          documentType: parseDocumentType(d.documentType || ''),
          documentNumber: d.documentNumber || '',
          documentExpiryDate: d.expiryDate || '',
        };
        const aiFieldCount = Object.values(aiData).filter(Boolean).length;
        const aiConfidence = typeof resp.confidence === 'number' ? resp.confidence : (50 + aiFieldCount * 8);

        // Merge: prefer MRZ data (name, doc number) if Tesseract found it, fill gaps with EasyOCR
        if (nativeResult && countExtractedFields(nativeResult) > 0) {
          const nr = nativeResult.extractedData;
          const merged = { ...aiData };
          if (nr.fullName) merged.fullName = nr.fullName;
          if (nr.documentNumber) merged.documentNumber = nr.documentNumber;
          if (nr.nationality) merged.nationality = nr.nationality;
          if (nr.dateOfBirth) merged.dateOfBirth = nr.dateOfBirth;
          if (nr.gender) merged.gender = nr.gender;
          if (nr.documentExpiryDate) merged.documentExpiryDate = nr.documentExpiryDate;
          if (nr.documentType) merged.documentType = nr.documentType;
          return { confidence: Math.max(aiConfidence, nativeResult.confidence, 80), method: 'MRZ' as const, extractedData: merged };
        }

        if (aiData.fullName || aiFieldCount >= 2) {
          return { confidence: Math.max(aiConfidence, 75), method: 'Text' as const, extractedData: aiData };
        }
        rememberBestResult({ confidence: aiConfidence, method: 'Text' as const, extractedData: aiData });
      }
    } catch (e: any) {
      // EasyOCR not available
    }

    if (nativeResult) {
      rememberBestResult(nativeResult);
    }

    // 3. Fall back to IZI vision scan (external AI-powered structured extraction)
    try {
      const result = await this.readDocument(file);
      if (result.confidence >= minimumAcceptedConfidence && countExtractedFields(result) > 0) {
        return result;
      }
      rememberBestResult(result);
    } catch (e: any) {
    }

    // Fallback: try AI analysis with explicit prompt
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('prompt',
        'Read this identity document. Return ONLY a JSON object with these fields: ' +
        'full_name, gender (M/F), date_of_birth (YYYY-MM-DD), nationality, ' +
        'document_type (passport/national_id/drivers_license), document_number, ' +
        'expiry_date (YYYY-MM-DD). No markdown, no explanation, just JSON.'
      );

      const response = await api.post('/api/v1/ocr/izi/analyze', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const fields = extractFields(response.data);
      const data = mapToFormData(fields);
      const fieldCount = Object.values(data).filter(Boolean).length;
      const confidence = extractConfidence(response.data, Math.min(90, 30 + fieldCount * 10));


      if (fieldCount > 0) {
        const result: OCRResult = {
          confidence,
          method: 'Text' as const,
          extractedData: data,
        };
        if (result.confidence >= minimumAcceptedConfidence) {
          return result;
        }
        rememberBestResult(result);
      }
    } catch (e: any) {
    }

    // Last resort: raw text OCR with regex
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/v1/ocr/izi/extract', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const text = extractRawText(response.data);
      const data: OCRResult['extractedData'] = {};

      const nameMatch = text.match(/(?:surname|nom|name)[:\s/]*([^\n]+)/i);
      if (nameMatch) data.fullName = nameMatch[1].trim();

      const genderMatch = text.match(/(?:sex|sexe|gender)[:\s/]*(M|F)/i);
      if (genderMatch) data.gender = parseGender(genderMatch[1]);

      const dobMatch = text.match(/(?:birth|naissance)[:\s/]*(\d{1,2}[/\-\.]\d{1,2}[/\-\.]\d{2,4})/i);
      if (dobMatch) data.dateOfBirth = parseDate(dobMatch[1]);

      const natMatch = text.match(/(?:nationality|nationalité)[:\s/]*([^\n]+)/i);
      if (natMatch) data.nationality = natMatch[1].trim();

      const docMatch = text.match(/(?:passport|document)\s*(?:no|n°|number)[:\s/]*([A-Z0-9]+)/i);
      if (docMatch) data.documentNumber = docMatch[1].trim();

      const fieldCount = Object.values(data).filter(Boolean).length;
      const confidence = extractConfidence(response.data, Math.min(70, 20 + fieldCount * 12));

      const result: OCRResult = {
        confidence,
        method: 'Text' as const,
        extractedData: data,
      };

      if (fieldCount > 0) {
        if (result.confidence >= minimumAcceptedConfidence) {
          return result;
        }
        rememberBestResult(result);
      }
    } catch {
      // Ignore here and fall back to the best prior OCR candidate if one exists.
    }

    if (bestResult) {
      return bestResult;
    }

    throw new Error('Impossible de lire le document. Veuillez saisir les informations manuellement.');
  },
};
